import { useEffect } from "react";

import { useStaticResource } from "../hooks/useStaticResource";
import { useHashTarget } from "../hooks/useHashTarget";
import { FoundingWall } from "./FoundingWall";
import { HonorableMentions } from "./HonorableMentions";
import { HowItWorks } from "./HowItWorks";
import { ClickRankings } from "./ClickRankings";
import { TodayBoard } from "./TodayBoard";
import { TomorrowAuction } from "./TomorrowAuction";
import { WhatIsDealbid } from "./WhatIsDealbid";
import type { SiteData } from "../site-data";

/**
 * The public homepage (`dealbid.app`) — the whole board argument, on one page.
 *
 * The order is canon decision `D-007`, and it is an argument rather than an arrangement:
 *
 *   1. **Today's Top 10**, with its sponsored disclosure. What the visitor came for, and
 *      `WEB-006`'s reason for existing — deal value is the design contract's first visual
 *      priority, so nothing precedes it.
 *   2. **Honorable Mentions**, every one core returns. Canon § 10 makes them "clearly
 *      separated from Top 10", and a reader can only see a separation where both lists are.
 *   3. **What Dealbid is**, then **tomorrow's auction**: the explanation and the live
 *      mechanism, for whoever is still reading. Demoted from the top by `WEB-006`, not
 *      removed.
 *   4. **Top deals by buyer clicks**, one section with a period selector, then **how it
 *      works**. `D-007` had moved the click-ranked lists up to sit directly beneath the paid
 *      board, on the operator's reading that canon § 11's contrast is read as one idea only
 *      when the two are adjacent; `D-012` reversed that and put the section where artboard 5
 *      draws it. What canon § 11 and § 14 actually require is that the earned list say what
 *      decided it, which it does in its heading, its description and core's own
 *      non-sponsorship sentence — none of which depend on how far up the page it sits.
 *   5. **Founding Deals**, last (`WEB-015`, canon decision `D-009`). The position is the
 *      decision: at the foot of the page, after the argument has been made, a permanent
 *      credit line reads as a credit line. Directly beneath the sponsored board it would
 *      read as a second, better board — which is exactly what canon § 12 is concerned
 *      about, and exactly what `D-009` says the wall is not.
 *
 * Subscribe and the footer are site-wide and close the page as they close every page.
 *
 * **Where the data comes from (`WEB-039`, canon decision `D-015`).** Four of the five public
 * reads are made by the *build* and travel in the bundle: the board, both click-ranked lists
 * and the Founding cohort. A visitor's page load issues no request for any of them. Canon § 7
 * locks a display day's positions for the whole day, so the board a build holds is the board
 * a reader would have fetched.
 *
 * `GET /v1/auction/tomorrow` is the exception and stays live, with its poll. It is the only
 * part of the page whose subject moves within a display day, and it is the only place a
 * countdown is measured — which is why no static section here has to derive one from a
 * `serverTime` that may be hours old.
 *
 * `board` is still read **once** and shared by the board, the mentions and the explanation,
 * because all three are views of the same response, and three copies could disagree.
 */
export function HomePage({
  onHonorableMentions,
  data,
}: {
  /**
   * Reports whether the board came back with any Honorable Mentions (`WEB-024`).
   *
   * The masthead drops its `Honorable Mentions` link when the section it targets is not on
   * the page, and it cannot find that out for itself: it is site-wide and the static pages
   * render it without fetching. This is the one place that already knows — the board is
   * loaded here and `HonorableMentions` decides from the same response.
   */
  onHonorableMentions?: ((present: boolean) => void) | undefined;
  /**
   * The build-time reads, handed down from `app/page.tsx` (`D-015`, `D-020`).
   *
   * Required rather than defaulted since `WEB-044`: the reads used to be importable from
   * anywhere through a Vite virtual module, and now the only way this section sees them is by
   * being given them. The suite drives every board state by passing one, without a network and
   * without stubbing a module.
   */
  data: SiteData;
}) {
  const board = useStaticResource(data.board);

  // Every section is a fragment target again (`D-007`). The build-time sections are ready on
  // the first render, so the hash can be resolved immediately. See `src/hooks/useHashTarget.ts`.
  useHashTarget(true);

  // The same condition `HonorableMentions` renders on, reported rather than duplicated: if
  // the section is on the page, the link to it is in the masthead, and neither can drift.
  const mentionsPresent = data.board.honorableMentions.length > 0;

  useEffect(() => {
    onHonorableMentions?.(mentionsPresent);
  }, [onHonorableMentions, mentionsPresent]);

  return (
    <main id="main">
      {/*
        Canon `D-015` § 3 and `D-019`. If the build that should run at the auction boundary
        did not, the page would otherwise serve an earlier day's board with that day's date
        printed on it, all day, with nothing to notice. The board's own dateline carries the
        correction — the browser can tell without asking core, because canon § 8 makes the
        display day UTC-canonical — so the page does not present an earlier day as today.
      */}
      <TodayBoard resource={board} />
      <HonorableMentions resource={board} />
      <WhatIsDealbid resource={board} />
      <TomorrowAuction />
      <ClickRankings weekly={data.weekly} monthly={data.monthly} builtAt={data.builtAt} />
      <HowItWorks />
      <FoundingWall cohort={data.founding} />
    </main>
  );
}
